import React, { useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { ToastContainer } from 'react-toastify';
import { cancelAcoinPendingRedemption } from '@homeplay/api';
import {
  SiteContext,
  resetAuthError,
  resetUnitError,
  selectUnitToken,
  selectUnitId,
  selectUser,
  selectSupportParams,
  selectSiteId,
} from '@homeplay/app';
import { useDepositGlobalNotification } from '@homeplay/hooks';
import Loader from '../UI/Loader';
import Maintenance from './Maintenance';
import IdleModal from '../IdleModal';
import ErrorModal from '../Modal/ErrorModal';
import PromoBonusesModal from '../Modal/PromoBonusesModal';
import ChatWidget from '../LiveChatWidget';
// import TestPanel from '../TestPanel';
import 'react-toastify/dist/ReactToastify.css';

const Layout = (props) => {
  const { siteName, isTouchUnit } = useContext(SiteContext);
  const loadingMessage = useSelector((state) => state.unit.loadingMessage);
  const authError = useSelector((state) => state.auth.error);
  const unitError = useSelector((state) => state.unit.error);
  const unitToken = useSelector(selectUnitToken);
  const unitId = useSelector(selectUnitId);
  const siteId = useSelector(selectSiteId);
  const user = useSelector(selectUser);
  const supportParams = useSelector(selectSupportParams);
  const loading = useSelector(
    (state) => state.unit.loading || state.auth.loading
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  useDepositGlobalNotification();

  const isUserLogin = !!user && !loading;
  const isGamePage = location.pathname.includes('/game/') || location.pathname.includes('/crash-games') || location.pathname.includes('/fafi') || location.pathname.includes('/lns');

  const handleClickAuth = () => {
    navigate(`/${siteName ? siteName + '/' : ''}auth`);
  };

  const error = unitError || authError;

  useEffect(() => {
    if (unitToken) {
      cancelAcoinPendingRedemption(unitToken);
    }
  }, [unitToken]);

  const handleCloseError = () => {
    if (error.redirect) {
      if (error.redirect === 'reload') {
        window.location.reload();
      } else {
        navigate(error.redirect);
      }
    }
    if (unitError) {
      dispatch(resetUnitError());
    }
    if (authError) {
      dispatch(resetAuthError());
    }
  };

  if (error && error.additionalInfo === 'server_offline') {
    return <Maintenance />;
  }

  return (
    <>
      <ToastContainer
        theme="colored"
        draggablePercent={25}
        autoClose={10_000}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
      />

      {props.children}

      {!error ? loading && <Loader message={loadingMessage} /> : null}

      <CSSTransition in={!!error} timeout={0} classNames="modal" unmountOnExit>
        <ErrorModal error={error} onClose={handleCloseError} />
      </CSSTransition>

      {isTouchUnit && <IdleModal />}
      <PromoBonusesModal />
      {/* <TestPanel /> */}

      {/* NOTE: Rendered at Layout level to prevent re-mounting on navigation */}
      {unitId && !isTouchUnit && supportParams?.useChat && (
        <ChatWidget
          title="Welcome to HomePlay!"
          primaryColor="#a9499d"
          accountId={
            supportParams.accountId
              ? supportParams.accountId
              : process.env.REACT_APP_LIVECHAT_ACCOUNT_ID
          }
          greeting="Hi there! How can I help you?"
          awayMessage="Sorry, we're not available at the moment!"
          newMessagePlaceholder="Start typing..."
          newMessagesNotificationText="View new messages"
          authMessage="You should login to start conversation"
          requireEmailUpfront={false}
          requireAuth={true}
          customer={
            user && !loading
              ? {
                  phone: user.phone,
                  external_id: user.id,
                  metadata: {
                    site_name: siteName ?? process.env.REACT_APP_SITE_NAME,
                    unit_id: unitId,
                    site_id: siteId,
                  },
                }
              : null
          }
          baseUrl={process.env.REACT_APP_LIVECHAT_BASE_URL}
          iframeUrlOverride={process.env.REACT_APP_LIVECHAT_IFRAME_URL}
          unitToken={unitToken}
          siteId={siteId}
          apiUrl={process.env.REACT_APP_API_URL}
          iconVariant="filled"
          onClickAuth={handleClickAuth}
          isUserLogin={isUserLogin}
          hidden={isGamePage}
        />
      )}
    </>
  );
};

export default Layout;
